<template>
  <div
    class="app-table"
    :class="{ 'app-table--borderless': borderless }"
    :style="cssVars"
  >
    <div
      v-if="headers && headers.length"
      class="app-table__head"
      role="row"
    >
      <div
        v-for="(h, idx) in headers"
        :key="`${h.label || ''}-${idx}`"
        class="app-table__th"
        :class="thClass(h, idx)"
        role="columnheader"
        :title="h.title || ''"
      >
        <button
          v-if="h.sortable && h.field"
          type="button"
          class="app-table__th-btn"
          :class="{ 'app-table__th-btn--active': h.field === sortField }"
          :aria-sort="ariaSortFor(h.field)"
          @click="onSortClick(h.field)"
        >
          <span class="app-table__th-label">{{ h.label }}</span>
          <span class="app-table__sort-icon" aria-hidden="true">
            <i
              v-if="h.field === sortField && sortDirection === 'asc'"
              class="fa-solid fa-sort-up"
            ></i>
            <i
              v-else-if="h.field === sortField && sortDirection === 'desc'"
              class="fa-solid fa-sort-down"
            ></i>
            <i v-else class="fa-solid fa-sort"></i>
          </span>
        </button>
        <template v-else>
          <slot name="header" :header="h" :index="idx">{{ h.label }}</slot>
        </template>
      </div>
    </div>

    <slot />
  </div>
</template>

<script>
/**
 * App data-table — outer chrome + sticky header for grid-based data tables.
 *
 * The component owns:
 *   - Card frame (border, radius, soft shadow, overflow-hidden so child
 *     hover-bg gets clipped at the corners).
 *   - Sticky uppercase header row.
 *   - Grid columns shared with descendant rows via a CSS custom property
 *     (`--app-table-cols`). Row components read this var in their own
 *     grid-template-columns rule, so the column definition lives in ONE
 *     place — the caller.
 *
 * Usage:
 *   <app-table
 *     :columns="'2fr 90px 70px 70px 110px 90px 50px'"
 *     :headers="[
 *       { label: 'Event' },           // first column defaults to left
 *       { label: 'Type' },            // others default to center
 *       { label: 'Status' },
 *       { label: 'Payment', align: 'right' },
 *       { label: '' },                // empty label for icon-only column
 *     ]"
 *   >
 *     <empty-state v-if="!rows.length" ... />
 *     <my-row v-for="row in rows" :key="row.id" :row="row" />
 *   </app-table>
 *
 * Row components must use the shared CSS var for their grid:
 *
 *   .my-row__grid {
 *     grid-template-columns: var(--app-table-cols, /* fallback *\/);
 *   }
 */
export default {
  name: 'AppTable',

  props: {
    // CSS grid-template-columns value applied to header + rows.
    // Example: "2fr 90px 70px 70px 110px 90px 50px".
    columns: {
      type: String,
      required: true,
    },

    // Array of header descriptors. First column defaults to left-align;
    // all others default to center-align. Each descriptor:
    //   { label, align?, title?, sortable?, field? }
    // Pass `sortable: true` + `field: 'someKey'` to make the header
    // clickable; the component emits `sort` with `{ field, direction }`.
    headers: {
      type: Array,
      required: true,
    },

    // Current sort state — controlled by the consumer.
    sortField: {
      type: String,
      default: '',
    },
    sortDirection: {
      type: String,
      default: '',
      validator: (v) => ['', 'asc', 'desc'].includes(v),
    },

    // Flatten the table: drop the surface background and elevation, keep the
    // hairline frame + radius so the head still reads as the table's top edge.
    // Use when the table already sits inside its own card-style frame.
    borderless: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['sort'],

  computed: {
    cssVars() {
      return { '--app-table-cols': this.columns }
    },
  },

  methods: {
    thClass(h, idx) {
      const align = h.align || (idx === 0 ? 'left' : 'center')
      return [
        `app-table__th--${align}`,
        { 'app-table__th--sortable': h.sortable && h.field },
      ]
    },

    ariaSortFor(field) {
      if (field !== this.sortField) return 'none'
      if (this.sortDirection === 'asc') return 'ascending'
      if (this.sortDirection === 'desc') return 'descending'
      return 'none'
    },

    // Toggle asc → desc → asc on repeat clicks; new field always starts asc.
    onSortClick(field) {
      let direction = 'asc'
      if (this.sortField === field) {
        direction = this.sortDirection === 'asc' ? 'desc' : 'asc'
      }
      this.$emit('sort', { field, direction })
    },
  },
}
</script>

<style lang="scss" scoped>
// Self-contained tokens — mirror admin-dashboard's `$ad-*` palette without
// importing its theme module so the component stays portable.
$at-bg-surface:   #ffffff;
$at-bg-soft:      #f6f8fa;
$at-border:       rgba(0, 0, 0, 0.08);
$at-text:         #2b3a4a;
$at-text-muted:   #6c7a89;
$at-text-subtle:  #9aa6b3;
$at-blue:         #1f73c1;
$at-radius-lg:    12px;
$at-shadow-sm:    0 1px 2px rgba(0, 0, 0, 0.04);
$at-duration:     140ms;
$at-ease:         cubic-bezier(.4, 0, .2, 1);

.app-table {
  background: $at-bg-surface;
  border: 0.5px solid $at-border;
  border-radius: $at-radius-lg;
  overflow: hidden;
  box-shadow: $at-shadow-sm;

  // Borderless variant — for tables that already sit inside a card. Drops the
  // surface fill and elevation, but keeps the hairline frame: without it the
  // head's soft strip detaches from the rows and the table reads as two
  // stacked blocks instead of one.
  &--borderless {
    background: transparent;
    box-shadow: none;

    // Rows render as standalone cards on phones — an outer frame around them
    // would double the border.
    @media (max-width: 600px) {
      border: none;
      border-radius: 0;
      overflow: visible;
    }
  }

  &__head {
    // Radius lives on the head itself, not only on the clipped card frame, so
    // the soft strip always reads as the table's top edge.
    border-radius: $at-radius-lg $at-radius-lg 0 0;
    display: grid;
    grid-template-columns: var(--app-table-cols);
    background: $at-bg-soft;
    border-bottom: 0.5px solid $at-border;
    padding: 0 12px;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  &__th {
    font-size: 11px;
    font-weight: 700;
    color: $at-text-muted;
    padding: 11px 8px;
    text-transform: uppercase;
    letter-spacing: .05em;
    user-select: none;

    &--left   { text-align: left; }
    &--center { text-align: center; }
    &--right  { text-align: right; }
  }

  // Sortable header button — fills the cell so the whole TH is clickable.
  &__th-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: transparent;
    border: none;
    padding: 0;
    margin: 0;
    color: inherit;
    font: inherit;
    text-transform: inherit;
    letter-spacing: inherit;
    font-weight: inherit;
    cursor: pointer;
    transition: color $at-duration $at-ease;

    &:hover { color: $at-text; }

    &:focus-visible {
      outline: 2px solid $at-blue;
      outline-offset: 2px;
      border-radius: 3px;
    }

    &--active { color: $at-text; }
  }

  // Center-aligned headers need the button centered within the cell.
  &__th--center &__th-btn { justify-content: center; }
  &__th--right  &__th-btn { justify-content: flex-end; }

  &__sort-icon {
    font-size: 10px;
    line-height: 1;
    color: $at-text-subtle;
    transition: color $at-duration $at-ease, opacity $at-duration $at-ease;
    opacity: 0.7;

    .app-table__th-btn:hover & { opacity: 1; }

    .app-table__th-btn--active & {
      color: $at-blue;
      opacity: 1;
    }
  }

  // On phones the grid header is unreadable — hide and let rows render in
  // their own card-style mobile layout (each row component owns this).
  @media (max-width: 600px) {
    &__head { display: none; }
  }
}
</style>
